import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Drop, Bell, Play, Pause, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const HydrationReminderPage = () => {
  const navigate = useNavigate();
  const [interval, setInterval] = useState(60); // minutes
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [nextReminderTime, setNextReminderTime] = useState(null);
  const [waterCount, setWaterCount] = useState(0);

  useEffect(() => {
    if (isActive && nextReminderTime) {
      const timer = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((nextReminderTime - now) / 1000 / 60));
        setTimeRemaining(remaining);

        if (remaining === 0) {
          handleReminder();
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isActive, nextReminderTime]);

  const startReminder = () => {
    const nextTime = Date.now() + interval * 60 * 1000;
    setNextReminderTime(nextTime);
    setTimeRemaining(interval);
    setIsActive(true);
    toast.success(`💧 Hydration reminder set for every ${interval} minutes!`);
  };

  const handleReminder = () => {
    // Show notification
    toast('💧 Time to Hydrate!', {
      description: 'Drink some water! Your body needs it.',
      duration: 10000,
      action: {
        label: 'Done',
        onClick: () => handleDrankWater()
      }
    });

    // Play sound (if supported)
    if ('Audio' in window) {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {
        // Audio not supported
      }
    }

    // Set next reminder
    const nextTime = Date.now() + interval * 60 * 1000;
    setNextReminderTime(nextTime);
    setTimeRemaining(interval);
  };

  const handleDrankWater = () => {
    setWaterCount(prev => prev + 1);
    toast.success('🎉 Great job staying hydrated!');
  };

  const pauseReminder = () => {
    setIsActive(false);
    toast.info('⏸️ Hydration reminders paused');
  };

  const stopReminder = () => {
    setIsActive(false);
    setNextReminderTime(null);
    setTimeRemaining(null);
    toast.info('Hydration reminder stopped');
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💧</span>
              <h1 className="text-2xl font-bold text-gradient">
                Hydration Reminder
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <div className="mb-8 p-6 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-3xl text-white text-center">
          <div className="text-6xl mb-3 emoji-bounce">💦</div>
          <h2 className="text-2xl font-bold mb-3">Stay Hydrated!</h2>
          <p className="text-white/90 leading-relaxed">
            Partying can dehydrate you fast. Set regular reminders to drink water and keep your body happy! 💙
          </p>
        </div>

        {waterCount > 0 && (
          <div className="mb-6 p-4 bg-[rgb(var(--success))]/20 border border-[rgb(var(--success))]/30 rounded-2xl text-center">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-[rgb(var(--text-primary))] font-bold">
              {waterCount} {waterCount === 1 ? 'drink' : 'drinks'} today!
            </p>
            <p className="text-sm text-[rgb(var(--text-secondary))]">Keep it up!</p>
          </div>
        )}

        {!isActive ? (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[rgb(var(--text-secondary))] mb-3">
                Remind me every:
              </label>
              <div className="grid grid-cols-4 gap-3">
                {[30, 60, 90, 120].map((time) => (
                  <button
                    key={time}
                    onClick={() => setInterval(time)}
                    data-testid={`interval-${time}`}
                    className={`p-4 rounded-xl border transition-all ${
                      interval === time
                        ? 'bg-cyan-500/20 border-cyan-500 text-[rgb(var(--text-primary))]'
                        : 'bg-[rgb(var(--surface))] border-white/5 text-[rgb(var(--text-secondary))] hover:border-cyan-500/30'
                    }`}
                  >
                    <div className="text-2xl font-semibold">{time}</div>
                    <div className="text-xs">min</div>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={startReminder}
              data-testid="start-hydration-reminder"
              className="w-full min-h-[64px] rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold text-lg shadow-xl btn-hover"
            >
              <Play size={24} weight="fill" className="mr-2" />
              Start Hydration Reminders
            </Button>

            <div className="mt-8 space-y-3">
              <h3 className="text-lg font-bold text-[rgb(var(--text-primary))]">💡 Hydration Tips</h3>
              {[
                'Drink water between alcoholic drinks',
                'Carry a water bottle with you',
                'Add electrolytes if dancing/sweating',
                'Don\'t chug - sip slowly and steadily',
                'Urine should be light yellow, not dark'
              ].map((tip, idx) => (
                <div key={idx} className="flex items-start p-3 bg-[rgb(var(--surface))] rounded-xl border border-white/5">
                  <Drop size={20} className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-[rgb(var(--text-secondary))] text-sm">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 mb-4 animate-pulse">
                  <Bell size={64} weight="duotone" className="text-white" />
                </div>
              </div>
              <div className="mb-4">
                <div className="text-6xl font-bold text-cyan-400 mb-2">
                  {timeRemaining}
                </div>
                <div className="text-[rgb(var(--text-secondary))]">
                  minutes until next reminder
                </div>
              </div>
              <p className="text-[rgb(var(--text-secondary))] max-w-md mx-auto">
                You'll get a notification when it's time to hydrate! 💧
              </p>
            </div>

            <Button
              onClick={handleDrankWater}
              data-testid="drank-water-button"
              className="w-full min-h-[64px] rounded-full bg-[rgb(var(--success))] hover:bg-[rgb(var(--success))]/90 text-white font-bold text-lg btn-hover"
            >
              <Drop size={24} weight="fill" className="mr-2" />
              I Just Drank Water!
            </Button>

            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={pauseReminder}
                variant="outline"
                className="min-h-[56px] rounded-full border-[rgb(var(--text-secondary))]/30 text-[rgb(var(--text-secondary))]"
              >
                <Pause size={20} className="mr-2" />
                Pause
              </Button>
              
              <Button
                onClick={stopReminder}
                variant="outline"
                className="min-h-[56px] rounded-full border-[rgb(var(--danger))]/30 text-[rgb(var(--danger))]"
              >
                <X size={20} className="mr-2" />
                Stop
              </Button>
            </div>

            <div className="p-6 bg-cyan-500/10 border border-cyan-500/30 rounded-2xl">
              <h3 className="font-bold text-[rgb(var(--text-primary))] mb-2">⚠️ Dehydration Warning Signs</h3>
              <ul className="space-y-1 text-sm text-[rgb(var(--text-secondary))]">
                <li>• Dry mouth, dizziness, headache</li>
                <li>• Dark yellow urine</li>
                <li>• Feeling tired or confused</li>
                <li>• Rapid heartbeat</li>
              </ul>
              <p className="text-xs text-cyan-400 mt-3">If you notice these, drink water immediately!</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HydrationReminderPage;
